import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { IconBellOutline18 } from 'nucleo-ui-outline-18'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { SettingsPage, SettingsRow, SettingsSection } from '~/components/settings/settings-page'
import { Spinner } from '~/components/ui/spinner'
import { Switch } from '~/components/ui/switch'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'
import type { NotificationCategory, NotificationChannel } from '~convex/lib/notificationEnums'

export const Route = createFileRoute('/_authenticated/$workspaceSlug/account/notifications')({
  component: NotificationSettings,
  head: () => ({ meta: [{ title: titleFor(msg`Notification settings`) }] }),
})

const CATEGORY_COPY: Record<
  NotificationCategory,
  { description: MessageDescriptor; title: MessageDescriptor }
> = {
  assignments: {
    description: msg`When a task is assigned to you.`,
    title: msg`Task assignments`,
  },
  clientActivity: {
    description: msg`Briefing submissions and accepted proposals.`,
    title: msg`Client activity`,
  },
  deadlinesVisits: {
    description: msg`Due dates and scheduled site visit reminders.`,
    title: msg`Deadlines and visits`,
  },
  repliesMentions: {
    description: msg`Direct replies and mentions in task comments.`,
    title: msg`Replies and mentions`,
  },
}

type Preference = {
  category: NotificationCategory
  inApp: boolean
  nativePush: boolean
}

function NotificationSettings() {
  const { t, i18n } = useLingui()
  const { data: preferences } = useQuery(convexQuery(api.notificationPreferences.list, {}))
  const setTimezone = useConvexMutation(api.notificationPreferences.setTimezone)
  const { mutate, isPending } = useMutation({
    mutationFn: useConvexMutation(api.notificationPreferences.set),
    onError: () => toast.error(t`Couldn't update notification preferences.`),
  })

  useEffect(() => {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions()
    if (timeZone) {
      void setTimezone({ timeZone }).catch(() => {
        // The Brazil-first fallback remains valid when this opportunistic
        // refresh cannot reach Convex.
      })
    }
  }, [setTimezone])

  const update = (category: NotificationCategory, channel: NotificationChannel, enabled: boolean) =>
    mutate({ category, channel, enabled })

  return (
    <SettingsPage>
      <SettingsSection
        description={t`These choices follow your account across every studio you belong to.`}
        icon={IconBellOutline18}
        title={t`What reaches you`}
      >
        {preferences === undefined ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          (preferences as Preference[]).map((preference) => {
            const copy = CATEGORY_COPY[preference.category]
            const categoryTitle = i18n._(copy.title)

            return (
              <SettingsRow
                description={i18n._(copy.description)}
                key={preference.category}
                label={categoryTitle}
                wide
              >
                <div className="grid w-full grid-cols-2 gap-6 sm:w-80">
                  <div className="flex items-center justify-end gap-2 whitespace-nowrap text-right text-muted-foreground text-xs">
                    <Trans>In Planno</Trans>
                    <Switch
                      aria-label={t`Show ${categoryTitle} in Planno`}
                      checked={preference.inApp}
                      disabled={isPending}
                      onCheckedChange={(enabled) => update(preference.category, 'inApp', enabled)}
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2 whitespace-nowrap text-right text-muted-foreground text-xs">
                    <Trans>Mobile push</Trans>
                    <Switch
                      aria-label={t`Send ${categoryTitle} as mobile push`}
                      checked={preference.nativePush}
                      disabled={isPending}
                      onCheckedChange={(enabled) =>
                        update(preference.category, 'nativePush', enabled)
                      }
                    />
                  </div>
                </div>
              </SettingsRow>
            )
          })
        )}
      </SettingsSection>
    </SettingsPage>
  )
}
